/**
 * `list.includes(item)` without the scan, for the article lists the derivation
 * asks the same membership question of thousands of times (a 10k-article blanket
 * statement's `selectedArticles`, one test per finding per cover: ~10⁸
 * comparisons, the review page's freeze on sandbox DEC-2026-0007).
 *
 * The Set is built once per list and kept beside it, keyed by the list's own
 * identity. The lists are query-cache data and the arrays the derivation builds
 * from it, and they MUST NEVER be changed in place after they are handed out:
 * the length check below only notices a list that GREW since its first read; an
 * element replaced at the same length would go unseen and answer stale. Same
 * answer as `includes` for every input (`Set` and `includes` both compare with
 * SameValueZero).
 */
const SETS = new WeakMap<
  readonly string[],
  { length: number; set: Set<string> }
>()

/** Below this, the scan is cheaper than building and holding a Set. */
const SCAN_MAX = 16

export function memberOf(list: readonly string[], item: string): boolean {
  if (list.length <= SCAN_MAX) return list.includes(item)
  let cached = SETS.get(list)
  if (!cached || cached.length !== list.length) {
    cached = { length: list.length, set: new Set(list) }
    SETS.set(list, cached)
  }
  return cached.set.has(item)
}
